import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Contact.css';

/*
 ┌─────────────────────────────────────────────────────────┐
 │  EMAILJS SETUP — 3 simple steps (free, no backend):     │
 │                                                         │
 │  1. Go to https://www.emailjs.com and sign up free      │
 │  2. Add Gmail service → connect shrisaimanpower...      │
 │  3. Create an Email Template with these variables:      │
 │       {{from_name}}  {{from_phone}}  {{company}}        │
 │       {{from_email}} {{service}}     {{message}}        │
 │       {{to_email}}                                      │
 │  4. Replace the 3 placeholders below with your IDs      │
 └─────────────────────────────────────────────────────────┘
*/
const EMAILJS_SERVICE_ID  = 'YOUR_SERVICE_ID';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'YOUR_TEMPLATE_ID';  // e.g. 'template_xyz789'
const EMAILJS_PUBLIC_KEY  = 'YOUR_PUBLIC_KEY';   // e.g. 'aBcDeFgHiJkLmNoP'

const RECIPIENT = 'shrisaimanpowerservices7@gmail.com';

const SERVICES = [
  'Skilled Manpower', 'Semi-Skilled Labour', 'Unskilled Labour',
  'Housekeeping Services', 'Industrial Cleaning', 'Loading & Unloading',
  'Material Handling', 'Driver Services', 'Recruitment Services', 'Multiple Services',
];

const EMPTY = { name: '', phone: '', company: '', email: '', service: '', message: '' };

const Contact = () => {
  const formRef = useRef(null);
  const [form, setForm]       = useState(EMPTY);
  const [status, setStatus]   = useState('idle'); // idle | sending | success | error
  const [errMsg, setErrMsg]   = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');

    const templateParams = {
      to_email:   RECIPIENT,
      from_name:  form.name,
      from_phone: form.phone,
      company:    form.company || 'Not provided',
      from_email: form.email  || 'Not provided',
      service:    form.service || 'Not specified',
      message:    form.message || 'No additional details.',
      reply_to:   form.email  || RECIPIENT,
    };

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      setStatus('success');
      setForm(EMPTY);
      // Auto-reset after 8 seconds
      setTimeout(() => setStatus('idle'), 8000);
    } catch (err) {
      console.error('EmailJS error:', err);
      setErrMsg('Failed to send. Please call us directly at +91 96891 00663.');
      setStatus('error');
    }
  };

  return (
    <section className="contact section-gray" id="contact">
      <div className="container contact-grid">

        {/* ── Left: Contact Info ── */}
        <div>
          <span className="section-label">Get In Touch</span>
          <h2 className="section-title">Let's Discuss<br /><span>Your Requirements</span></h2>
          <div className="divider" />

          <div className="contact-info">
            <div className="ci-item">
              <div className="ci-icon">📍</div>
              <div>
                <div className="ci-label">Address</div>
                <div className="ci-value">Chakan, Pune, Maharashtra, India</div>
              </div>
            </div>
            <div className="ci-item">
              <div className="ci-icon">📞</div>
              <div>
                <div className="ci-label">Phone</div>
                <a href="tel:+919689100663" className="ci-value ci-link">+91 96891 00663</a>
                <a href="tel:+919767577713" className="ci-value ci-link">+91 97675 77713</a>
              </div>
            </div>
            <div className="ci-item">
              <div className="ci-icon">✉️</div>
              <div>
                <div className="ci-label">Email</div>
                <a href="mailto:shrisaimanpowerservices7@gmail.com" className="ci-value ci-link-red" style={{ fontSize: '13px' }}>
                  shrisaimanpowerservices7@gmail.com
                </a>
              </div>
            </div>
            <div className="ci-item">
              <div className="ci-icon">🕐</div>
              <div>
                <div className="ci-label">Business Hours</div>
                <div className="ci-value">Mon–Sat: 9:00 AM – 7:00 PM</div>
              </div>
            </div>
          </div>

          <div className="map-placeholder">
            <div style={{ fontSize: 32 }}>📍</div>
            <div>Chakan, Pune, Maharashtra</div>
            <a href="https://maps.google.com/?q=Rajgurunagar,Pune" target="_blank" rel="noreferrer" className="map-link">
              Open in Google Maps →
            </a>
          </div>
        </div>

        {/* ── Right: Form ── */}
        <div className="contact-form">
          <div className="form-title">Request a Free Quote</div>
          <p className="form-subtitle">Fill the form below and we'll email you back within a few hours.</p>

          {/* Success banner */}
          {status === 'success' && (
            <div className="form-success">
              <span className="form-success-icon">✅</span>
              <div>
                <strong>Enquiry sent successfully!</strong><br />
                We've received your request and will contact you within 24 hours.<br />
                For urgent needs, call <a href="tel:+919689100663">+91 96891 00663</a>.
              </div>
            </div>
          )}

          {/* Error banner */}
          {status === 'error' && (
            <div className="form-error">
              <span>⚠️</span>
              <div>{errMsg}</div>
            </div>
          )}

          {status !== 'success' && (
            <form ref={formRef} onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label>Your Name <span className="req">*</span></label>
                  <input
                    name="name" value={form.name} onChange={handleChange}
                    placeholder="Full name" required
                    disabled={status === 'sending'}
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number <span className="req">*</span></label>
                  <input
                    name="phone" value={form.phone} onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX" required
                    disabled={status === 'sending'}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Company / Factory Name</label>
                <input
                  name="company" value={form.company} onChange={handleChange}
                  placeholder="Your company or factory name"
                  disabled={status === 'sending'}
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="your@company.com"
                  disabled={status === 'sending'}
                />
              </div>

              <div className="form-group">
                <label>Service Required</label>
                <select
                  name="service" value={form.service} onChange={handleChange}
                  disabled={status === 'sending'}
                >
                  <option value="">Select a service...</option>
                  {SERVICES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Your Requirements</label>
                <textarea
                  name="message" value={form.message} onChange={handleChange}
                  placeholder="Number of workers needed, location, duration, shift timings, etc."
                  disabled={status === 'sending'}
                />
              </div>

              <button type="submit" className="btn-submit" disabled={status === 'sending'}>
                {status === 'sending' ? (
                  <span className="sending-state">
                    <span className="spinner" /> Sending...
                  </span>
                ) : (
                  '📧 Send Enquiry'
                )}
              </button>

              <p className="form-note">
                Your information is kept private and will only be used to respond to your enquiry.
              </p>
            </form>
          )}
        </div>

      </div>
    </section>
  );
};

export default Contact;
